import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/auth-context';
import { PageHeader } from '../../../components/PageHeader';
import { SpinnerBlock } from '../../../components/Spinner';
import { getDisplayError } from '@utils/get-display-error';
import { Toggle } from '../../../components/toggle/Toggle';
import { type FeatureFlag, fetchFeatureFlags, updateFeatureFlag } from '../models';
import { trackEvent } from '../../../utils/track-event';
import { ErrorState } from '../../../components/error-state/ErrorState';
import { InternalRole } from '../../auth/endpoints/MeEndpoint.types';
import { roleToNumber } from '../../auth/roles.client';

export const FeatureFlagsPage: React.FC = () => {
  const { me } = useAuth();

  if (roleToNumber(me.role) < roleToNumber(InternalRole.InternalAdminPlus)) {
    return <Navigate to="/internal" replace />;
  }

  return (
    <div className="p-4">
      <PageHeader title="Feature Flags" />

      <div className="mb-4">
        <h1 className="text-2xl font-semibold heading-color">Feature Flags</h1>
        <p className="text-sm secondary-color">Secret toggles for controlled feature rollout.</p>
      </div>

      <FeatureFlagsList />
    </div>
  );
};

const FeatureFlagsList: React.FC = () => {
  const { data: flags, error, isLoading, mutate } = useSWR('feature-flags', fetchFeatureFlags);

  if (isLoading) {
    return <SpinnerBlock message="Loading feature flags..." />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => mutate()} variant="section" />;
  }

  if (!flags || flags.length === 0) {
    return <p className="secondary-color">No feature flags configured.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {flags.map((flag) => (
        <FeatureFlagRow key={flag.id} flag={flag} onToggle={mutate} />
      ))}
    </div>
  );
};

const FeatureFlagRow: React.FC<{ flag: FeatureFlag; onToggle: () => void }> = ({ flag, onToggle }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleToggle = async () => {
    try {
      setIsUpdating(true);
      await updateFeatureFlag(flag.id, !flag.enabled);
      trackEvent('feature_flag_toggled', {
        flag_id: flag.id,
        enabled: !flag.enabled,
      });
      onToggle();
      toast.success(`${flag.id} ${!flag.enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(`Failed to update flag: ${getDisplayError(err)}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex items-center justify-between rounded-lg border p-4 card-bg">
      <div>
        <div className="font-medium heading-color">{flag.id}</div>
        {flag.description && <div className="text-sm secondary-color">{flag.description}</div>}
      </div>

      <Toggle isEnabled={flag.enabled} onChange={handleToggle} isDisabled={isUpdating} />
    </div>
  );
};
